import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
  VersionTimeline,
  cn,
  type VersionSourceTone,
} from "@nexus/ui";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { formatCompactLocalDateTime } from "@/features/opportunities/lib/format-relative-time.js";
import type { PersonaBriefDto } from "@/features/persona-opportunities/api/persona-opportunity-briefs.api";
import {
  personaBriefVersionHistoryQueryOptions,
  personaBriefVersionQueryOptions,
  useRestorePersonaBriefVersion,
} from "@/lib/queries/persona-opportunity-briefs.queries";

import { PersonaScriptFieldDiff } from "./PersonaScriptFieldDiff.js";

const SOURCE_I18N: Record<string, { key: string; fallback: string }> = {
  GENERATED: { key: "personaOpportunities.scripts.history.sourceGenerated", fallback: "Generated" },
  MANUAL: { key: "personaOpportunities.scripts.history.sourceManual", fallback: "Manual" },
  AI_EDIT: { key: "personaOpportunities.scripts.history.sourceAiEdit", fallback: "AI edit" },
  RESTORED: { key: "personaOpportunities.scripts.history.sourceRestored", fallback: "Restored" },
};

const SOURCE_TONE: Record<string, VersionSourceTone> = {
  GENERATED: "generated",
  MANUAL: "manual",
  AI_EDIT: "ai",
  RESTORED: "restored",
};

const CHANGE_LABEL_I18N: Record<string, { key: string; fallback: string }> = {
  Title: { key: "personaOpportunities.scripts.history.title", fallback: "Title" },
  Duration: { key: "personaOpportunities.scripts.briefs.duration", fallback: "Duration" },
  "Age range": { key: "personaOpportunities.scripts.briefs.ageRange", fallback: "Age range" },
  Gender: { key: "personaOpportunities.scripts.briefs.gender", fallback: "Gender" },
  "Hook package": {
    key: "personaOpportunities.scripts.briefs.hookPackage",
    fallback: "Hook package",
  },
  "Selected hook": {
    key: "personaOpportunities.scripts.briefs.history.selectedHook",
    fallback: "Selected hook",
  },
  "Concept direction": {
    key: "personaOpportunities.scripts.briefs.history.conceptDirection",
    fallback: "Concept direction",
  },
  "Concept line": {
    key: "personaOpportunities.scripts.briefs.history.conceptLine",
    fallback: "Concept line",
  },
  "CTA direction": {
    key: "personaOpportunities.scripts.briefs.history.ctaDirection",
    fallback: "CTA direction",
  },
  "CTA line": {
    key: "personaOpportunities.scripts.briefs.history.ctaLine",
    fallback: "CTA line",
  },
  Notes: { key: "personaOpportunities.scripts.briefs.history.notes", fallback: "Notes" },
  Preferences: {
    key: "personaOpportunities.scripts.briefs.preferences",
    fallback: "Preferences",
  },
  "Product link": {
    key: "personaOpportunities.scripts.briefs.productLink",
    fallback: "Product link",
  },
  Font: { key: "personaOpportunities.scripts.briefs.font", fallback: "Font" },
  Logo: { key: "personaOpportunities.scripts.briefs.logo", fallback: "Logo" },
  Disclaimer: { key: "personaOpportunities.scripts.briefs.disclaimer", fallback: "Disclaimer" },
  "Brand reset": {
    key: "personaOpportunities.scripts.briefs.history.brandReset",
    fallback: "Brand reset",
  },
  "AI edit": {
    key: "personaOpportunities.scripts.history.sourceAiEdit",
    fallback: "AI edit",
  },
  "AI edit · Concept direction": {
    key: "personaOpportunities.scripts.briefs.history.aiConceptDirection",
    fallback: "AI edit · Concept direction",
  },
  "AI edit · Concept line": {
    key: "personaOpportunities.scripts.briefs.history.aiConceptLine",
    fallback: "AI edit · Concept line",
  },
  "AI edit · CTA direction": {
    key: "personaOpportunities.scripts.briefs.history.aiCtaDirection",
    fallback: "AI edit · CTA direction",
  },
  "AI edit · CTA line": {
    key: "personaOpportunities.scripts.briefs.history.aiCtaLine",
    fallback: "AI edit · CTA line",
  },
  "AI edit · Notes": {
    key: "personaOpportunities.scripts.briefs.history.aiNotes",
    fallback: "AI edit · Notes",
  },
  "AI edit · Preferences": {
    key: "personaOpportunities.scripts.briefs.history.aiPreferences",
    fallback: "AI edit · Preferences",
  },
};

const MANUAL_HOOK_CHANGE_LABEL = /^Hook ([1-4]) · (direction|line)$/;
const AI_HOOK_CHANGE_LABEL = /^AI edit · Hook ([1-4]) · (Direction|Line)$/;
const RESTORED_CHANGE_LABEL = /^Restored from v([1-9]\d*)$/;

function translateBriefChangeLabel(label: string | null, t: TFunction): string | null {
  if (!label) return null;

  const fixedCopy = CHANGE_LABEL_I18N[label];
  if (fixedCopy) return t(fixedCopy.key, fixedCopy.fallback);

  const manualHook = MANUAL_HOOK_CHANGE_LABEL.exec(label);
  if (manualHook) {
    const index = manualHook[1];
    return manualHook[2] === "direction"
      ? t("personaOpportunities.scripts.briefs.history.hookDirection", {
          defaultValue: "Hook {{index}} direction",
          index,
        })
      : t("personaOpportunities.scripts.briefs.history.hookLine", {
          defaultValue: "Hook {{index}} line",
          index,
        });
  }

  const aiHook = AI_HOOK_CHANGE_LABEL.exec(label);
  if (aiHook) {
    const index = aiHook[1];
    return aiHook[2] === "Direction"
      ? t("personaOpportunities.scripts.briefs.history.aiHookDirection", {
          defaultValue: "AI edit · Hook {{index}} · Direction",
          index,
        })
      : t("personaOpportunities.scripts.briefs.history.aiHookLine", {
          defaultValue: "AI edit · Hook {{index}} · Line",
          index,
        });
  }

  const restored = RESTORED_CHANGE_LABEL.exec(label);
  if (restored) {
    return t("personaOpportunities.scripts.briefs.history.restored", {
      defaultValue: "Restored version {{version}}",
      version: restored[1],
    });
  }

  return t("personaOpportunities.scripts.briefs.history.unknownChange", "Updated");
}

type PreviewGetter = (brief: PersonaBriefDto) => string;

type PreviewField = {
  key: string;
  labelKey: string;
  fallback: string;
  value: PreviewGetter;
};

export interface BriefsHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setId: string;
  tenantId: string;
  brief: PersonaBriefDto;
  canRestore: boolean;
}

export function BriefsHistoryDrawer({
  open,
  onOpenChange,
  setId,
  tenantId,
  brief,
  canRestore,
}: BriefsHistoryDrawerProps) {
  const { t } = useTranslation();
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const restore = useRestorePersonaBriefVersion();
  const restoreInFlight = useRef(false);

  const historyQuery = useQuery(
    personaBriefVersionHistoryQueryOptions(tenantId, setId, brief.id, { enabled: open }),
  );
  const versions = historyQuery.data?.versions ?? [];
  const latestVersion = historyQuery.data?.activeVersion ?? brief.version;

  useEffect(() => {
    if (!open) {
      setSelectedVersion(null);
      return;
    }
    if (selectedVersion == null && latestVersion) {
      setSelectedVersion(latestVersion);
    }
  }, [open, latestVersion, selectedVersion]);

  const previewQuery = useQuery(
    personaBriefVersionQueryOptions(tenantId, setId, brief.id, selectedVersion, {
      enabled: open && selectedVersion != null,
    }),
  );
  const originalQuery = useQuery(
    personaBriefVersionQueryOptions(tenantId, setId, brief.id, 1, {
      enabled: open,
    }),
  );
  const previousVersion =
    selectedVersion != null && selectedVersion > 1 ? selectedVersion - 1 : null;
  const previousQuery = useQuery(
    personaBriefVersionQueryOptions(tenantId, setId, brief.id, previousVersion, {
      enabled: open && previousVersion != null,
    }),
  );

  const preview = previewQuery.isPlaceholderData ? undefined : previewQuery.data?.brief;
  const original = originalQuery.data?.brief;
  const previous =
    previousVersion != null &&
    !previewQuery.isPlaceholderData &&
    !previousQuery.isPlaceholderData
      ? previousQuery.data?.brief
      : undefined;
  const isLatest = selectedVersion != null && selectedVersion === latestVersion;
  const title = (preview ?? brief).videoName;
  const duration = preview?.duration;
  const viewingVersion = selectedVersion ?? latestVersion;
  const selectedInstruction =
    versions.find((entry) => entry.version === viewingVersion)?.instruction ?? null;
  const versionCount = versions.length || brief.version;

  const timelineVersions = useMemo(
    () =>
      versions.map((entry) => {
        return {
          id: entry.id ?? `v${entry.version}`,
          version: entry.version,
          createdAt: entry.createdAt,
          createdAtLabel: formatCompactLocalDateTime(entry.createdAt, Date.now()),
          createdBy:
            entry.createdBy === "creative_engine"
              ? t("personaOpportunities.scripts.history.creativeEngine", "Creative engine")
              : entry.createdBy,
          sourceBadge: SOURCE_I18N[entry.source]
            ? t(SOURCE_I18N[entry.source].key, SOURCE_I18N[entry.source].fallback)
            : entry.source,
          sourceTone: SOURCE_TONE[entry.source],
          changeLabel: translateBriefChangeLabel(entry.changeLabel, t),
          detail: entry.instruction,
          isCurrentState: entry.version === latestVersion,
        };
      }),
    [latestVersion, t, versions],
  );

  const activeVersionId =
    selectedVersion != null
      ? (versions.find((entry) => entry.version === selectedVersion)?.id ?? `v${selectedVersion}`)
      : undefined;

  function handleRestore() {
    if (selectedVersion == null || isLatest || restore.isPending || restoreInFlight.current) {
      return;
    }
    restoreInFlight.current = true;
    restore.mutate(
      { setId, briefId: brief.id, version: selectedVersion, tenantId },
      {
        onSuccess: (updated) => {
          setSelectedVersion(updated.version);
        },
        onSettled: () => {
          restoreInFlight.current = false;
        },
      },
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-[1200px] flex-col gap-0 p-0 sm:max-w-[1200px]"
      >
        <SheetHeader className="shrink-0 gap-0 px-6 py-[18px]">
          <div className="flex min-w-0 items-center gap-2 pr-8">
            <SheetTitle className="truncate text-[15px] font-semibold">{title}</SheetTitle>
            <Badge variant="default" className="rounded-full px-2 py-px text-[11px]">
              {t("personaOpportunities.scripts.history.versionCount", {
                defaultValue_one: "{{count}} version",
                defaultValue_other: "{{count}} versions",
                count: versionCount,
              })}
            </Badge>
          </div>
          <SheetDescription className="mt-[3px] text-xs text-muted-foreground">
            {t("personaOpportunities.scripts.history.subtitle", "Version history")}
          </SheetDescription>
        </SheetHeader>

        <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[minmax(0,1fr)_280px]">
          <div className="min-h-0 overflow-y-auto border-b border-border px-6 py-5 md:border-b-0 md:border-r">
            <div className="mb-3.5 flex min-h-8 items-start justify-between gap-3.5">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold">
                    {t("personaOpportunities.scripts.history.viewing", {
                      defaultValue: "Viewing v{{version}}",
                      version: selectedVersion ?? latestVersion,
                    })}
                  </p>
                  {isLatest ? (
                    <span className="rounded-full bg-ai-subtle px-2 py-px text-[10px] font-medium text-ai-subtle-foreground">
                      {t("personaOpportunities.scripts.history.current", "Current")}
                    </span>
                  ) : null}
                </div>
                {duration ? (
                  <p className="mt-[3px] text-xs text-muted-foreground italic">{duration}</p>
                ) : null}
                {preview && previous && previewDiffersFromPrevious(preview, previous) ? (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {t(
                      "personaOpportunities.scripts.history.diffLegend",
                      "Struck text was removed. Underlined text is the new wording.",
                    )}
                  </p>
                ) : null}
                {selectedInstruction ? (
                  <div className="mt-2.5">
                    <p className="mb-1 font-mono text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
                      {t("personaOpportunities.scripts.history.prompt", "Prompt")}
                    </p>
                    <p className="text-xs italic leading-relaxed break-words whitespace-pre-wrap text-ai-text">
                      {t("personaOpportunities.scripts.history.promptQuote", {
                        defaultValue: "“{{prompt}}”",
                        prompt: selectedInstruction,
                      })}
                    </p>
                  </div>
                ) : null}
              </div>
              {canRestore && !isLatest && selectedVersion != null ? (
                <Button
                  size="sm"
                  iconStart="rotate-ccw"
                  onClick={handleRestore}
                  disabled={restore.isPending}
                >
                  {t("personaOpportunities.scripts.history.useThisVersion", "Use this version")}
                </Button>
              ) : null}
            </div>

            {previewQuery.isError ? (
              <div className="flex flex-col items-start gap-2">
                <p role="alert" className="text-sm text-muted-foreground">
                  {t(
                    "personaOpportunities.scripts.briefs.history.previewLoadError",
                    "Couldn't load this version.",
                  )}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void previewQuery.refetch()}
                >
                  {t("personaOpportunities.scripts.history.retry", "Retry")}
                </Button>
              </div>
            ) : !preview ? (
              <div
                className="flex flex-col gap-3"
                aria-label={t(
                  "personaOpportunities.scripts.history.loadingPreview",
                  "Loading preview…",
                )}
              >
                <Skeleton className="h-36 w-full rounded-sm" />
                <Skeleton className="h-36 w-full rounded-sm" />
              </div>
            ) : (
              <HistoryPreview brief={preview} original={original} previous={previous} />
            )}
          </div>

          <div className="flex min-h-0 flex-col">
            <div className="shrink-0 px-[18px] pt-4 pb-2">
              <p className="font-mono text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
                {t("personaOpportunities.scripts.history.versions", "Versions")}
              </p>
              <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                {t(
                  "personaOpportunities.scripts.history.selectHint",
                  "Select one to preview it",
                )}
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-[18px] pb-[18px]">
              {historyQuery.isLoading ? (
                <div
                  className="flex flex-col gap-2"
                  aria-label={t(
                    "personaOpportunities.scripts.history.loadingVersions",
                    "Loading versions…",
                  )}
                >
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : historyQuery.isError ? (
                <div className="flex flex-col items-start gap-2">
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "personaOpportunities.scripts.history.loadError",
                      "Couldn't load version history.",
                    )}
                  </p>
                  <Button size="sm" variant="outline" onClick={() => void historyQuery.refetch()}>
                    {t("personaOpportunities.scripts.history.retry", "Retry")}
                  </Button>
                </div>
              ) : (
                <VersionTimeline
                  variant="rail"
                  versions={timelineVersions}
                  activeVersionId={activeVersionId}
                  onSelect={(entry) => setSelectedVersion(entry.version)}
                  labels={{
                    latest: t("personaOpportunities.scripts.history.latest", "Latest"),
                    empty: t("personaOpportunities.scripts.history.empty", "No versions yet."),
                    select: t("personaOpportunities.scripts.history.select", "Select version"),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function highlightedClass(isDifferent: boolean): string {
  return isDifferent
    ? "border-l-2 border-l-ai bg-ai-subtle"
    : "border-l-2 border-l-transparent bg-background";
}

function hookText(
  brief: PersonaBriefDto | undefined,
  hookIndex: number,
  field: "direction" | "line",
): string {
  const hook = brief?.hooks.find((row) => row.hookIndex === hookIndex);
  return hook?.[field] ?? "";
}

function brandText(brief: PersonaBriefDto, field: keyof PersonaBriefDto["brand"]): string {
  return brief.brand[field] ?? "";
}

function previewFields(): PreviewField[] {
  const hooks: PreviewField[] = [1, 2, 3, 4].flatMap((index) => [
    {
      key: `hook-${index}-direction`,
      labelKey: "personaOpportunities.scripts.briefs.history.hookDirection",
      fallback: "Hook {{index}} direction",
      value: (brief) => hookText(brief, index, "direction"),
    },
    {
      key: `hook-${index}-line`,
      labelKey: "personaOpportunities.scripts.briefs.history.hookLine",
      fallback: "Hook {{index}} line",
      value: (brief) => hookText(brief, index, "line"),
    },
  ]);

  return [
    {
      key: "duration",
      labelKey: "personaOpportunities.scripts.briefs.duration",
      fallback: "Duration",
      value: (brief) => brief.duration,
    },
    {
      key: "hookPackage",
      labelKey: "personaOpportunities.scripts.briefs.hookPackage",
      fallback: "Hook package",
      value: (brief) => String(brief.hookPackage),
    },
    {
      key: "selectedHookIndex",
      labelKey: "personaOpportunities.scripts.briefs.history.selectedHook",
      fallback: "Selected hook",
      value: (brief) => String(brief.selectedHookIndex),
    },
    {
      key: "ageRange",
      labelKey: "personaOpportunities.scripts.briefs.ageRange",
      fallback: "Age range",
      value: (brief) => brief.creatorProfile.ageRange,
    },
    {
      key: "gender",
      labelKey: "personaOpportunities.scripts.briefs.gender",
      fallback: "Gender",
      value: (brief) => brief.creatorProfile.gender,
    },
    ...hooks,
    {
      key: "conceptDirection",
      labelKey: "personaOpportunities.scripts.briefs.history.conceptDirection",
      fallback: "Concept direction",
      value: (brief) => brief.concept.direction,
    },
    {
      key: "conceptLine",
      labelKey: "personaOpportunities.scripts.briefs.history.conceptLine",
      fallback: "Concept line",
      value: (brief) => brief.concept.line,
    },
    {
      key: "ctaDirection",
      labelKey: "personaOpportunities.scripts.briefs.history.ctaDirection",
      fallback: "CTA direction",
      value: (brief) => brief.cta.direction,
    },
    {
      key: "ctaLine",
      labelKey: "personaOpportunities.scripts.briefs.history.ctaLine",
      fallback: "CTA line",
      value: (brief) => brief.cta.line,
    },
    {
      key: "preferences",
      labelKey: "personaOpportunities.scripts.briefs.preferences",
      fallback: "Preferences",
      value: (brief) => brief.creatorProfile.preferences ?? "",
    },
    {
      key: "notes",
      labelKey: "personaOpportunities.scripts.briefs.notesTitle",
      fallback: "Notes for the creator",
      value: (brief) => brief.notes ?? "",
    },
    {
      key: "productLink",
      labelKey: "personaOpportunities.scripts.briefs.productLink",
      fallback: "Product link",
      value: (brief) => brandText(brief, "productLink"),
    },
    {
      key: "font",
      labelKey: "personaOpportunities.scripts.briefs.font",
      fallback: "Font",
      value: (brief) => brandText(brief, "font"),
    },
    {
      key: "logo",
      labelKey: "personaOpportunities.scripts.briefs.logo",
      fallback: "Logo",
      value: (brief) => brandText(brief, "logo"),
    },
    {
      key: "disclaimer",
      labelKey: "personaOpportunities.scripts.briefs.disclaimer",
      fallback: "Disclaimer",
      value: (brief) => brandText(brief, "disclaimer"),
    },
  ];
}

function previewDiffersFromPrevious(
  preview: PersonaBriefDto,
  previous: PersonaBriefDto,
): boolean {
  return previewFields().some((field) => field.value(preview) !== field.value(previous));
}

function HistoryPreview({
  brief,
  original,
  previous,
}: {
  brief: PersonaBriefDto;
  original: PersonaBriefDto | undefined;
  previous: PersonaBriefDto | undefined;
}) {
  const { t } = useTranslation();
  const empty = t("personaOpportunities.scripts.history.emDash", "—");
  const fields = previewFields();

  return (
    <dl className="overflow-hidden rounded-sm border border-border">
      {fields.map((field, index) => {
        const after = field.value(brief);
        const before = original ? field.value(original) : after;
        const isDifferent = original != null && after !== before;
        const previousValue = previous ? field.value(previous) : undefined;
        const hookIndex = field.key.startsWith("hook-")
          ? Number(field.key.split("-")[1])
          : undefined;

        return (
          <div
            key={field.key}
            className={cn(
              "px-3 py-[9px]",
              index < fields.length - 1 && "border-b border-border",
              highlightedClass(isDifferent),
            )}
          >
            <dt className="mb-1 font-mono text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
              {t(field.labelKey, {
                defaultValue: field.fallback,
                index: hookIndex,
              })}
            </dt>
            <dd className="text-xs leading-[1.55] whitespace-pre-wrap text-foreground">
              <PersonaScriptFieldDiff
                before={previousValue}
                after={after}
                empty={empty}
              />
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
